import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { IconChoices } from '../../../../components/common/Icon'
import { ButtonVariant } from '../../../../components/common/Button'
import { IllustrationChoices } from '../../../../components/common/Illustration'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { useModal } from '../../../../hooks/useModal'
// eslint-disable-next-line max-len
import { CreateUpdateLocalOfferModal } from '../../../../components/society/LocalOffer/CreateUpdateLocalOfferModal'
import { LocalOfferListItem } from '../../../../components/society/LocalOfferListItem'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { localOfferQueries } from '../../../../api/local-offers'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { i18nSocietyType } from '../../../../i18n'

export const LocalOffersView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const authUserId = useAuthenticatedUserId()
  const { society } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('society:local-offers', {
      '{{ societyName }}': society?.name,
    })
  )
  const {
    show: showCreateModal,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal()

  const isAdmin = isUserAdminInSociety(society, authUserId as string)

  const { data: localOffers, isLoading: isLocalOffersLoading } = useQuery(
    localOfferQueries.getLocalOffersPerSocieties(society?._id)
  )

  const loading = isLocalOffersLoading

  const errorView =
    !loading && localOffers && localOffers.length === 0
      ? {
          title: translate('localOffersView.emptyState.title'),
          subtitle: translate('localOffersView.emptyState.subtitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          illustration: IllustrationChoices.EMPTY,
        }
      : undefined

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('localOffersView.title')}
        errorView={errorView}
        buttons={
          isAdmin
            ? [
                {
                  label: translate('common.actions.add'),
                  variant: ButtonVariant.PRIMARY,
                  icon: IconChoices.PLUS_SIGN,
                  onClick: openCreateModal,
                },
              ]
            : undefined
        }
      >
        <div className="flex flex-wrap gap-4">
          {localOffers?.map((_offer) => (
            <LocalOfferListItem key={_offer._id} localOffer={_offer} />
          ))}
        </div>
      </ViewBase>
      {society && (
        <CreateUpdateLocalOfferModal
          society={society}
          show={showCreateModal}
          close={closeCreateModal}
        />
      )}
    </ErrorBoundary>
  )
}
